import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Container, Toast, TypoGraph } from 'components'
import { Checkbox, CircularProgress, Switch } from '@mui/material'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import { getCompanyMonitoring, getValidationTypes, saveCompanyMonitoring } from 'api'
import {
  ICompanyMonitoringData,
  ICompanyValidationTypeData,
  IRecipientCandidate,
  IValidationTypeData,
} from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import { isViewOnly } from 'tools/auth'
import OverrideInfoNote from '../overrideInfoNote'
import RecipientsInput from '../recipientsInput'
import ParametersInput from '../parametersInput'
import { hasParameterErrors } from '../parametersInput/validate'
import styles from './alertSettingsTab.module.scss'

const FM = strings.FLEET_MONITORING

const emptyDraft: ICompanyMonitoringData = {
  companyMonitoringId: null,
  active: true,
  defaultRecipients: [],
  distanceUnit: 'miles',
  validationTypes: [],
}

interface IAlertSettingsTabProps {
  candidates: IRecipientCandidate[]
}

const AlertSettingsTab: React.FunctionComponent<IAlertSettingsTabProps> = ({ candidates }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [validationTypes, setValidationTypes] = useState<IValidationTypeData[]>([])
  const [initial, setInitial] = useState<ICompanyMonitoringData>(emptyDraft)
  const [draft, setDraft] = useState<ICompanyMonitoringData>(emptyDraft)
  const [scrollToCode, setScrollToCode] = useState<string | null>(null)
  const readOnly = isViewOnly()

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [company, types] = await Promise.all([getCompanyMonitoring(), getValidationTypes()])
      const normalized: ICompanyMonitoringData = {
        companyMonitoringId: company.companyMonitoringId,
        active: company.active ?? true,
        defaultRecipients: company.defaultRecipients || [],
        distanceUnit: company.distanceUnit || 'miles',
        validationTypes: company.validationTypes || [],
      }
      setInitial(normalized)
      setDraft(normalized)
      setValidationTypes(types || [])
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.LOAD_ALERT_SETTINGS_FAILED })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  // When an alert with thresholds is turned on, scroll its (newly shown) thresholds into view.
  useEffect(() => {
    if (!scrollToCode) return
    const el = document.getElementById(`fm-thresholds-${scrollToCode}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setScrollToCode(null)
  }, [scrollToCode])

  const isDirty = useMemo(() => JSON.stringify(initial) !== JSON.stringify(draft), [initial, draft])

  // An enabled alert with an invalid threshold (required empty, out of range, bad length…) blocks saving.
  const hasInvalidThresholds = useMemo(
    () =>
      (draft.validationTypes || []).some(entry => {
        if (!entry.enabled) return false
        const vt = validationTypes.find(v => v.code === entry.code)
        if (!vt) return false
        return hasParameterErrors(vt.parameterDefs, entry.parameters || {}, { level: 'insurer' })
      }),
    [draft, validationTypes],
  )

  const findTypeEntry = (code: string) =>
    (draft.validationTypes || []).find(v => v.code === code)

  const updateTypeEntry = (code: string, patch: Partial<ICompanyValidationTypeData>) => {
    setDraft(prev => {
      const list = [...(prev.validationTypes || [])]
      const idx = list.findIndex(v => v.code === code)
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...patch }
      } else {
        const catalogItem = validationTypes.find(v => v.code === code)
        if (!catalogItem) return prev
        list.push({
          validationTypeId: catalogItem.validationTypeId,
          code,
          enabled: false,
          notifyOnResolve: false,
          recipients: [],
          ...patch,
        })
      }
      return { ...prev, validationTypes: list }
    })
  }

  const setRecipients = (userIds: string[]) => {
    setDraft(prev => ({ ...prev, defaultRecipients: userIds }))
  }

  const setAllValidationTypesEnabled = (enabled: boolean) => {
    setDraft(prev => {
      const existing = prev.validationTypes || []
      const byCode: Record<string, ICompanyValidationTypeData> = {}
      existing.forEach(v => {
        byCode[v.code] = v
      })
      const next = validationTypes.map(vt => {
        const current = byCode[vt.code]
        return current
          ? { ...current, enabled }
          : {
              validationTypeId: vt.validationTypeId,
              code: vt.code,
              enabled,
              notifyOnResolve: false,
              recipients: [],
            }
      })
      return { ...prev, validationTypes: next }
    })
  }

  const handleSave = async () => {
    if (hasInvalidThresholds) return
    setSaving(true)
    try {
      await saveCompanyMonitoring(draft)
      Toast({ type: 'success', subTitle: FM.TOASTS.SETTINGS_SAVED })
      await loadAll()
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || FM.ERRORS.SAVE_FAILED })
    } finally {
      setSaving(false)
    }
  }

  const handleDiscard = () => {
    setDraft(initial)
  }

  if (loading) {
    return (
      <Container className={styles.loader}>
        <CircularProgress />
      </Container>
    )
  }

  const enabledCount = (draft.validationTypes || []).filter(v => v.enabled).length
  const totalCount = validationTypes.length

  return (
    <div className={styles.root} data-testid="fleet-monitoring-settings-body">
      <TypoGraph
        variant="body2"
        content={FM.ALERT_SETTINGS.DESCRIPTION}
        className={styles.tabDescription}
      />
      <OverrideInfoNote />
      {isDirty && (
        <div className={styles.saveBar}>
          <span className={styles.saveBarMessage}>
            {hasInvalidThresholds ? FM.COMMON.SETTINGS_INCOMPLETE : FM.COMMON.UNSAVED_CHANGES}
          </span>
          <div className={styles.saveBarActions}>
            <Button label={FM.COMMON.DISCARD} variant="text" onClick={handleDiscard} disabled={saving} />
            <Button
              label={FM.COMMON.SAVE_CONFIGURATION}
              variant="contained"
              onClick={handleSave}
              disabled={saving || hasInvalidThresholds}
            />
          </div>
        </div>
      )}

      {/* Company settings */}
      <div className={styles.card}>
        <TypoGraph variant="h3" content={FM.ALERT_SETTINGS.COMPANY_SETTINGS_TITLE} className={styles.cardTitle} />
        <TypoGraph
          variant="body2"
          content={FM.ALERT_SETTINGS.COMPANY_SETTINGS_SUBTITLE}
          className={styles.cardSubtitle}
        />

        <div className={styles.fieldGroup}>
          <div className={styles.fieldLabel}>
            <span className={styles.fieldLabelText}>{FM.ALERT_SETTINGS.DEFAULT_RECIPIENTS}</span>
            <span className={styles.fieldLabelHint}>{FM.ALERT_SETTINGS.DEFAULT_RECIPIENTS_HINT}</span>
          </div>
          <RecipientsInput
            value={draft.defaultRecipients || []}
            onChange={setRecipients}
            candidates={candidates}
            disabled={readOnly}
          />
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.fieldLabel}>
            <span className={styles.fieldLabelText}>{FM.ALERT_SETTINGS.DISTANCE_UNIT}</span>
            <span className={styles.fieldLabelHint}>{FM.ALERT_SETTINGS.DISTANCE_UNIT_HINT}</span>
          </div>
          <div className={styles.unitToggle}>
            <button
              type="button"
              disabled={readOnly}
              className={`${styles.unitButton} ${draft.distanceUnit === 'miles' ? styles.unitButtonActive : ''}`}
              onClick={() => setDraft(prev => ({ ...prev, distanceUnit: 'miles' }))}
            >
              {FM.COMMON.MILES}
            </button>
            <button
              type="button"
              disabled={readOnly}
              className={`${styles.unitButton} ${draft.distanceUnit === 'kilometers' ? styles.unitButtonActive : ''}`}
              onClick={() => setDraft(prev => ({ ...prev, distanceUnit: 'kilometers' }))}
            >
              {FM.COMMON.KILOMETERS}
            </button>
          </div>
        </div>
      </div>

      {/* Delivery channels */}
      <div className={styles.card}>
        <TypoGraph variant="h3" content={FM.ALERT_SETTINGS.DELIVERY_CHANNELS_TITLE} className={styles.cardTitle} />
        <TypoGraph
          variant="body2"
          content={FM.ALERT_SETTINGS.DELIVERY_CHANNELS_SUBTITLE}
          className={styles.cardSubtitle}
        />
        <div className={styles.channelRow}>
          <div className={styles.channelIcon}>
            <MailOutlineIcon fontSize="small" />
          </div>
          <div className={styles.channelInfo}>
            <div className={styles.channelName}>{FM.ALERT_SETTINGS.EMAIL_NOTIFICATIONS}</div>
            <div className={styles.channelHint}>
              {FM.ALERT_SETTINGS.EMAIL_NOTIFICATIONS_HINT}
            </div>
          </div>
          <Switch
            checked={!!draft.active}
            disabled={readOnly}
            onChange={e => setDraft(prev => ({ ...prev, active: e.target.checked }))}
          />
        </div>
      </div>

      {/* Alerts list */}
      <div className={styles.alertsHeader}>
        <TypoGraph
          variant="h3"
          content={FM.ALERT_SETTINGS.ALERTS_HEADING(enabledCount, totalCount)}
          className={styles.sectionHeading}
        />
        {totalCount > 0 && !readOnly && (
          <div className={styles.alertsBulkActions}>
            <Button
              label={FM.ALERT_SETTINGS.ENABLE_ALL}
              variant="text"
              onClick={() => setAllValidationTypesEnabled(true)}
              disabled={enabledCount === totalCount}
            />
            <Button
              label={FM.ALERT_SETTINGS.DISABLE_ALL}
              variant="text"
              onClick={() => setAllValidationTypesEnabled(false)}
              disabled={enabledCount === 0}
            />
          </div>
        )}
      </div>
      {validationTypes.length === 0 ? (
        <div className={styles.recipientEmpty}>{FM.COMMON.NO_VALIDATION_TYPES}</div>
      ) : (
        validationTypes.map(vt => {
          const entry = findTypeEntry(vt.code)
          const enabled = !!entry?.enabled
          const notifyOnResolve = !!entry?.notifyOnResolve
          return (
            <div key={vt.code} className={styles.validationCard}>
              <div className={styles.validationBody}>
                <div className={styles.validationInfo}>
                  <div className={styles.validationHeader}>
                    <span className={styles.validationName}>{vt.name}</span>
                    {vt.severity === 'critical' && (
                      <span className={styles.severityCritical}>{FM.COMMON.CRITICAL}</span>
                    )}
                    {vt.severity === 'high' && (
                      <span className={styles.severityHigh}>{FM.COMMON.HIGH}</span>
                    )}
                    {vt.severity === 'medium' && (
                      <span className={styles.severityMedium}>{FM.COMMON.MEDIUM}</span>
                    )}
                    {vt.severity === 'low' && (
                      <span className={styles.severityLow}>{FM.COMMON.LOW}</span>
                    )}
                  </div>
                  {vt.description && (
                    <div className={styles.validationDescription}>{vt.description}</div>
                  )}
                  {vt.supportsResolveNotification && (
                    <label className={styles.notifyRow}>
                      <Checkbox
                        size="small"
                        checked={notifyOnResolve}
                        disabled={readOnly || !enabled}
                        onChange={e => updateTypeEntry(vt.code, { notifyOnResolve: e.target.checked })}
                      />
                      {FM.ALERT_SETTINGS.NOTIFY_ON_RESOLVE}
                    </label>
                  )}
                </div>
                <Switch
                  checked={enabled}
                  disabled={readOnly}
                  onChange={e => {
                    const checked = e.target.checked
                    updateTypeEntry(vt.code, { enabled: checked })
                    if (checked && vt.parameterDefs && vt.parameterDefs.length > 0) {
                      setScrollToCode(vt.code)
                    }
                  }}
                />
              </div>
              <div className={styles.validationRecipients}>
                <span className={styles.validationRecipientsLabel}>
                  {FM.ALERT_SETTINGS.VALIDATION_OWNERS}
                  <span className={styles.validationRecipientsHint}>
                    {FM.ALERT_SETTINGS.VALIDATION_OWNERS_HINT}
                  </span>
                </span>
                <RecipientsInput
                  value={entry?.recipients ?? []}
                  onChange={userIds => updateTypeEntry(vt.code, { recipients: userIds })}
                  candidates={candidates}
                  disabled={readOnly || !enabled}
                />
              </div>
              {enabled && vt.parameterDefs && vt.parameterDefs.length > 0 && (
                <div id={`fm-thresholds-${vt.code}`}>
                  <ParametersInput
                    defs={vt.parameterDefs}
                    value={entry?.parameters ?? {}}
                    level="insurer"
                    onChange={params => updateTypeEntry(vt.code, { parameters: params })}
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>
          )
        })
      )}
    </div>
  )
}

export default AlertSettingsTab
